import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Mail, X, AlertTriangle, ImageOff } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "replied", label: "Replied" },
  { value: "closed", label: "Closed" },
];
const STATUS_STYLE = {
  new: "bg-red-100 text-red-700",
  in_progress: "bg-amber-100 text-amber-700",
  replied: "bg-blue-100 text-blue-700",
  closed: "bg-zinc-200 text-zinc-600",
};

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/inquiries${filter ? `?status=${filter}` : ""}`);
      setInquiries(data.inquiries);
      setEmailConfigured(data.email_configured);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (inq, status) => {
    try {
      await api.patch(`/admin/inquiries/${inq.id}`, { status });
      setInquiries((list) => list.map((x) => (x.id === inq.id ? { ...x, status } : x)));
      if (selected?.id === inq.id) setSelected((s) => ({ ...s, status }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vehicleStr = (i) => [i.vehicle_year, i.vehicle_make, i.vehicle_model, i.trim].filter(Boolean).join(" ") || "—";

  return (
    <AdminLayout title="Customer Inquiries">
      {!emailConfigured && (
        <div className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3 text-sm" data-testid="inquiries-email-warning">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong>Email delivery is not configured.</strong> Inquiries are being saved here, but no notification emails are sent yet. Add a <span className="font-mono">RESEND_API_KEY</span> (and verify your sending domain) to enable automatic emails — no code changes needed.
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
          <span className="text-xs text-zinc-500 mr-1">Filter:</span>
          <button onClick={() => setFilter("")} className={`text-xs px-3 h-8 rounded ${filter === "" ? "bg-zinc-900 text-white" : "border border-zinc-200 hover:bg-zinc-50"}`}>All</button>
          {STATUSES.map((s) => (
            <button key={s.value} onClick={() => setFilter(s.value)} data-testid={`inq-filter-${s.value}`} className={`text-xs px-3 h-8 rounded ${filter === s.value ? "bg-zinc-900 text-white" : "border border-zinc-200 hover:bg-zinc-50"}`}>{s.label}</button>
          ))}
        </div>
        <table className="w-full text-sm" data-testid="inquiries-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-3 py-3">Name</th>
              <th className="text-left px-3 py-3">Email</th>
              <th className="text-left px-3 py-3">Vehicle</th>
              <th className="text-left px-3 py-3">Part</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-zinc-500">Loading…</td></tr>}
            {!loading && inquiries.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-zinc-500">No inquiries yet.</td></tr>}
            {inquiries.map((i) => (
              <tr key={i.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`inquiry-row-${i.id}`} onClick={() => setSelected(i)}>
                <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">{(i.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 font-medium">{i.name}{i.photos?.length ? <span className="ml-2 text-[10px] text-zinc-400">📎{i.photos.length}</span> : null}</td>
                <td className="px-3 py-3 text-zinc-600">{i.email}</td>
                <td className="px-3 py-3 text-zinc-600">{vehicleStr(i)}</td>
                <td className="px-3 py-3 text-zinc-600">{i.part_needed || "—"}</td>
                <td className="px-3 py-3 text-zinc-600 capitalize">{i.inquiry_type}</td>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <select value={i.status} onChange={(e) => setStatus(i, e.target.value)} data-testid={`inquiry-status-${i.id}`} className={`text-[11px] font-bold uppercase tracking-wide rounded px-2 py-1 border-0 cursor-pointer ${STATUS_STYLE[i.status] || ""}`}>
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); setSelected(i); }} className="text-xs text-red-600 hover:underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <InquiryDetail inquiry={selected} onClose={() => setSelected(null)} onStatus={(s) => setStatus(selected, s)} />}
    </AdminLayout>
  );
}

function InquiryDetail({ inquiry, onClose, onStatus }) {
  const field = (k, v) => v ? <div><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{k}</div><div className="text-sm text-zinc-800">{v}</div></div> : null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="inquiry-detail-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-md w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display text-lg">{inquiry.inquiry_type === "fitment" ? "Fitment Request" : "Contact Inquiry"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded" data-testid="inquiry-detail-close"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {field("Name", inquiry.name)}
            {field("Email", <a href={`mailto:${inquiry.email}`} className="text-red-600 hover:underline inline-flex items-center gap-1"><Mail size={12} />{inquiry.email}</a>)}
            {field("Part needed", inquiry.part_needed)}
            {field("Year", inquiry.vehicle_year)}
            {field("Make", inquiry.vehicle_make)}
            {field("Model", inquiry.vehicle_model)}
            {field("Trim", inquiry.trim)}
            {field("VIN", inquiry.vin)}
            {field("Product", inquiry.product_name)}
          </div>
          {inquiry.product_url && <a href={inquiry.product_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all">{inquiry.product_url}</a>}
          {inquiry.message && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Message</div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap bg-zinc-50 rounded-md p-3">{inquiry.message}</p>
            </div>
          )}
          {inquiry.photos?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Photos ({inquiry.photos.length})</div>
              <div className="flex flex-wrap gap-3" data-testid="inquiry-detail-photos">
                {inquiry.photos.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="w-24 h-24 rounded-md overflow-hidden border border-zinc-200 bg-zinc-50 flex items-center justify-center">
                    <img src={url} alt="inquiry" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
            <span className="text-xs text-zinc-500">Status:</span>
            {STATUSES.map((s) => (
              <button key={s.value} onClick={() => onStatus(s.value)} className={`text-xs px-3 h-8 rounded ${inquiry.status === s.value ? "bg-zinc-900 text-white" : "border border-zinc-200 hover:bg-zinc-50"}`}>{s.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
